// src/pages/About.tsx
import { User, Heart, Award, Calendar, Target } from 'lucide-react';

const About = () => {
  const timeline = [
    {
      year: '2021',
      title: 'Ustanovitev Allmaverse',
      description: 'Odprtje ateljeja v Mariboru in začetek umetniške poti'
    },
    {
      year: '2020',
      title: 'Vrnitev v Slovenijo',
      description: 'Po študiju v Benetkah se vrnem domov z novo vizijo'
    },
    {
      year: '2018-2020',
      title: 'Študij v Benetkah',
      description: 'Specializacija za likovno umetnost in oblikovanje'
    },
    {
      year: '2017',
      title: 'Začetek umetniške poti',
      description: 'Prvi koraki v svet profesionalne umetnosti'
    }
  ];

  const values = [
    {
      icon: Target,
      title: 'Dostopnost',
      description: 'Umetnost mora biti dostopna vsem, ne glede na ozadje ali izkušnje'
    },
    {
      icon: Heart,
      title: 'Pristnost',
      description: 'Vsako delo nosi del moje duše in edinstveno zgodbo'
    },
    {
      icon: Award,
      title: 'Kakovost',
      description: 'Uporabljam le najboljše materiale in tehnike za dolgotrajne rezultate'
    },
    {
      icon: Calendar,
      title: 'Trajnost',
      description: 'Odgovorno do okolja z uporabo ekoloških materialov'
    }
  ];

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Hero Section */}
        <section className="text-center mb-12">
          <div className="max-w-4xl mx-auto">
            <h1 
              className="text-5xl md:text-7xl font-lora font-bold bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent"
              style={{ 
                lineHeight: 1.2,
                paddingBottom: '0.1em',
                marginBottom: '0.5rem'
              }}
            >
              O nas
            </h1>
            <p className="text-xl md:text-2xl text-white/80 leading-relaxed">
              Spoznajte zgodbo, vizijo in vrednote, ki stojijo za Allmaverse
            </p>
          </div>
        </section>

        {/* Founder Story */}
        <section className="mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="glass-card p-8 rounded-3xl h-96 bg-linear-to-br from-purple-400/10 to-pink-400/10 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-linear-to-br from-purple-400/30 to-pink-400/30 flex items-center justify-center">
                    <User className="w-16 h-16 text-white" />
                  </div>
                  <p className="text-white/60 italic">Alma Lovrenčič</p>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2 space-y-6">
              <h2 
                className="text-4xl md:text-5xl font-lora font-bold bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent"
                style={{ 
                  lineHeight: 1.2,
                  paddingBottom: '0.1em',
                  marginBottom: '1.5rem'
                }}
              >
                Moja zgodba
              </h2>
              <p className="text-lg text-white/80 leading-relaxed">
                Moja umetniška pot se je začela z globoko ljubeznijo do barv in oblik. 
                Po študiju v čudovitih Benetkah, kjer sem se naučila tradicionalnih tehnik 
                in sodobnih pristopov, sem se vrnila v Maribor z jasno vizijo.
              </p>
              <p className="text-lg text-white/80 leading-relaxed">
                Allmaverse ni le atelje - to je vesolje ustvarjalnosti, kjer se srečujejo 
                tradicija in inovacija. Verjamem, da ima vsak človek v sebi umetnika, 
                in moja naloga je, da mu pomagam, da se izrazi.
              </p>
              <blockquote className="border-l-4 border-purple-400 pl-6 py-4 italic text-white/70 text-lg">
                "Umetnost ni luksuz, ampak potreba duše."
              </blockquote>
            </div>
          </div>
        </section>

        {/* What is Allmaverse */}
        <section className="mb-20">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-lora font-bold bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent"
              style={{ 
                lineHeight: 1.2,
                paddingBottom: '0.1em',
                marginBottom: '1.5rem'
              }}
            >
              Kaj je Allmaverse?
            </h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <p className="text-xl text-white/90 leading-relaxed font-medium">
                Allmaverse = Universe of Art, Craft and Design
              </p>
              <p className="text-lg text-white/80 leading-relaxed">
                To je prostor brez ustvarjalnih omejitev, kjer se prepletajo različne 
                umetniške discipline. Od slikarstva do keramike, od oblikovanja do svetovanja - 
                tu se rodi umetnost, ki govori neposredno srcu.
              </p>
              <p className="text-lg text-white/80 leading-relaxed">
                Naša filozofija temelji na prepričanju, da mora umetnost služiti življenju, 
                ne le okrasiti prostore. Vsako delo, ki nastane v Allmaverse, nosi zgodbo 
                in čustvo, ki se prenese na svojega lastnika.
              </p>
            </div>
            <div className="glass-card p-8 rounded-3xl bg-linear-to-br from-purple-400/10 to-pink-400/10">
              <div className="text-center">
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="glass-card p-4 rounded-2xl">
                    <h4 className="text-lg font-semibold text-white mb-2">Slikarstvo</h4>
                    <p className="text-white/70 text-sm">Klasično in sodobno</p>
                  </div>
                  <div className="glass-card p-4 rounded-2xl">
                    <h4 className="text-lg font-semibold text-white mb-2">Keramika</h4>
                    <p className="text-white/70 text-sm">Funkcionalna umetnost</p>
                  </div>
                  <div className="glass-card p-4 rounded-2xl">
                    <h4 className="text-lg font-semibold text-white mb-2">Tekstil</h4>
                    <p className="text-white/70 text-sm">Umetnost za vsakdan</p>
                  </div>
                  <div className="glass-card p-4 rounded-2xl">
                    <h4 className="text-lg font-semibold text-white mb-2">Svetovanje</h4>
                    <p className="text-white/70 text-sm">Strokovna pomoč</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="mb-20">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-lora font-bold bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent"
              style={{ 
                lineHeight: 1.2,
                paddingBottom: '0.1em',
                marginBottom: '1.5rem'
              }}
            >
              Moja pot
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Ključni mejniki v moji umetniški karieri
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {timeline.map((item) => (
                <div key={item.year} className="glass-card-hover p-6 flex flex-col md:flex-row items-start md:items-center gap-6">
                  <div className="shrink-0">
                    <div className="w-20 h-20 rounded-full bg-linear-to-br from-purple-400/30 to-pink-400/30 flex items-center justify-center">
                      <span className="text-lg font-bold text-white">{item.year}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                    <p className="text-white/80 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="mb-20">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-lora font-bold bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent"
              style={{ 
                lineHeight: 1.2,
                paddingBottom: '0.1em',
                marginBottom: '1.5rem'
              }}
            >
              Naše vrednote
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Principi, ki vodijo naše delo in ustvarjanje
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div 
                  key={value.title}
                  className="glass-card-hover p-6 text-center"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-linear-to-br from-purple-400/30 to-pink-400/30 flex items-center justify-center">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-4">{value.title}</h3>
                  <p className="text-white/70 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center">
          <div className="glass-card p-12 rounded-3xl max-w-4xl mx-auto">
            <h2 
              className="text-3xl md:text-4xl font-lora font-bold bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent"
              style={{ 
                lineHeight: 1.2,
                paddingBottom: '0.1em',
                marginBottom: '1.5rem'
              }}
            >
              Pridružite se naši umetniški skupnosti
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              Ne glede na to, ali iščete umetniško delo ali se želite naučiti nekaj novega
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="glass-card-hover px-8 py-4 text-lg font-medium text-white">
                Rezerviraj delavnico
              </button>
              <button className="glass-card px-8 py-4 text-lg font-medium text-white hover:bg-white/20 transition-all duration-300 border-2 border-purple-400/50">
                Kontaktiraj me
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;